let helpers = {
    /**
     * The Line Factor is a is fudging number used to scale font sizes
     * @type: {number}
     */
    lineFactor: .15,

    /**
     * Get the estimated size of a printed line based on the font size
     * This is because the font size is not the actual size printed on the PDF in points.
     * TODO: This should be reviewed and made more scalable if necessary
     * @param {number} fontSize - the size of the font
     * @returns {number}
     */
    getLineOffset: function (fontSize) {
        return fontSize * (1 + helpers.lineFactor)
    },

    /**
     * Get the estimated size of spacing between lines for single line spacing
     * This is because the size of a line doesn't include the space between it.
     * TODO: This should be reviewed and made more scalable if necessary
     * @param {number} fontSize - the size of the font
     * @returns {number}
     */
    getLineSpacing: function (fontSize) {
        return (fontSize / 11);
    },

    /**
     * This function will get the absolute top position of a control taking into account the pointer and printing bounds...EK
     * @param {Object} control - Control to get the position of.
     * @param {Object} ctx - The current reporting context.
     * @returns {Number}
     */
    getRenderTop: function(control, ctx){
        return ctx.printBounds.top + ctx.rootContext.ptr.top + control.runTime.top;
    },

    /**
     * This function will get the absolute left position of a control taking into account the pointer and printing bounds...EK
     * @param control - Control to get the position of.
     * @param {Object} ctx - The current reporting context.
     * @returns {Number}
     */
    getRenderLeft: function(control, ctx){
        return ctx.printBounds.left + ctx.rootContext.ptr.left + control.Left;
    },

    canvasCtx: document.createElement('canvas').getContext('2d'),

    /**
     * Take specified text and wrap as necessary to fit the defined width.
     * Return object with any new line(s) and metrics.
     * @param {string} fontName - The font name the text will be rendered in.
     * @param {number} fontSize - the font's size (in PIXEL).
     * @param {string} fontWeight - The font weight (bold, normal, etc.)
     * @param {string} text - The text to fit and wrap.
     * @param {number} toWidth - The width (max) the text should fit into.
     * @param {number} textMargin - The margin around the text (if any) to fit the text into a parent control.
     * @returns {{lineWidths: Array, width: *, lines: Array, height: number}}
     */
    fitTextLine: function(fontName, fontSize, fontWeight, text, toWidth, textMargin) {
        // Split raw text by line breaks, and then fit text by line within the width...EK
        var res = {
            width: toWidth,
            height: -1,
            lines: [],
            lineWidths: []
        };

        toWidth = toWidth - (textMargin * 2);       // We remove the text margin for the effective size...EK

        //Update the font of the measure canvas if the font definition is different...EK
        var fontDef = fontWeight + ' ' + fontSize + 'px ' + fontName;       // Note: The order that these settings are set are important...EK
        if (helpers.canvasCtx.font != fontDef){ helpers.canvasCtx.font = fontDef }
        var textSize = helpers.canvasCtx.measureText(text).width;

        if (textSize > toWidth) {
            //We need to wrap this text...EK
            var words = text.split(' '),
                line = '',
                lineWidth = 0,
                wordWidth = 0,
                spaceWidth = helpers.canvasCtx.measureText(' ').width;        //Cache the length of a space so we can use it in our word width later...EK

            for (let w = 0, wl = words.length; w < wl; w++){
                let word = words[w];

                wordWidth = Math.floor(helpers.canvasCtx.measureText(word).width);

                if (lineWidth + wordWidth > toWidth){       //Don't take space width into account for the line "visible" size...EK
                    //Check to see if the word is longer than the entire width... If so, split the word and continue...EK
                    if (wordWidth > toWidth){
                        //We have a single word that is too long for the line, we need to split it up by size not spaces...EK

                        let widthLeftOnLine = toWidth - lineWidth;
                        //We should then pull the characters that will fit on the current line, and then update the word and start a new line with that word...EK
                        for (let c = 0, cl = word.length; c < cl; c++) {
                            let char = word[c];
                            let charWidth = helpers.canvasCtx.measureText(char).width;
                            widthLeftOnLine -= charWidth;

                            if (widthLeftOnLine > 0) {
                                //We still have space left, add this char to the line and continue...EK
                                lineWidth += charWidth;
                                line += char;
                            }
                            else {
                                //This char wont fit fully, need to move to the next line...EK
                                //Replace the current word with the word with only non-accounted for characters...EK
                                words[w] = word.substring(c + 1);

                                res.lines.push(line);
                                res.lineWidths.push(lineWidth);
                                line = char;
                                lineWidth = charWidth;
                                w--;        // Reset the word index so this word is accounted for again with the remaining chars...EK
                                break;
                            }
                        }
                    }
                    else {
                        line = line.trim();     // Be sure that we remove the extra space for when we are rebuilding the lines above...EK
                        res.lines.push(line);
                        res.lineWidths.push(lineWidth);
                        line = word + ' ';
                        lineWidth = wordWidth + spaceWidth;
                    }
                    //Next Line...EK
                }
                else {
                    line += word + ' ';
                    lineWidth += wordWidth + spaceWidth;
                }
            }

            if (line !== ''){
                line = line.trim();     // Be sure that we remove the extra space for when we are rebuilding the lines above...EK
                res.lines.push(line);
                res.lineWidths.push(lineWidth - spaceWidth);
            }
        }
        else {
            //This is a single line text that fits within the width and margin... Just return as is...EK
            res.lines.push(text);
            res.lineWidths.push(textSize);
        }

        res.height = fontSize * res.lines.length + (textMargin * 2);

        return res;
    },

    /**
     * Will take text with font information and will fit it to a width, while wrapping the text into lines for render...EK
     * @param {string} fontName - The font name the text will be rendered in.
     * @param {number} fontSize - the font's size (in PIXEL).
     * @param {string} fontWeight - The font weight (bold, normal, etc.)
     * @param {string} text - The text to fit and wrap.
     * @param {number} toWidth - The width (max) the text should fit into.
     * @param {number} textMargin - The margin around the text (if any) to fit the text into a parent control.
     * @returns {{width: number, height: number, lines: Array}}
     */
    fitText: function(fontName, fontSize, fontWeight, text, toWidth, textMargin) {
        // Fit a specific line within a specified width...EK

        // correct line breaks for consistent by OS...EK
        text = text.replace(/\r\n|\r/g, '\n')
        let lineBreakLines = text.split('\n');

        if (lineBreakLines.length <= 1) {
            // This has text, but no hard breaks...EK
            return helpers.fitTextLine(fontName, fontSize, fontWeight, text, toWidth, textMargin);
        }
        else {
            let res = null;

            // This text has hard breaks, collate each hard break line to make sure they fit into the width...EK
            lineBreakLines.forEach((line) => {
                let lineRes = helpers.fitTextLine(fontName, fontSize, fontWeight, line, toWidth, textMargin);

                if (res == null) {
                    res = lineRes;
                }
                else {
                    // Merge the global result with this line result...EK
                    res.lines = res.lines.concat(lineRes.lines);
                    res.lineWidths = res.lineWidths.concat(lineRes.lineWidths);
                    res.height += lineRes.height;

                    if (res.width < lineRes.width) {
                        res.width = lineRes.width;
                    }
                }
            });

            // reset the height so that the textMargin is only accounted for once (it is used in the fitTextLine).
            res.height = fontSize * res.lines.length + (textMargin * 2);

            return res;
        }
    }
};

export {
    helpers
};
